import 'cross-fetch/polyfill';
import lodashGet from 'lodash/get';
import isUndefined from 'lodash/isUndefined';
import merge from 'lodash/merge';
import omitBy from 'lodash/omitBy';
import throttle from 'lodash/throttle';

import store, {AppDispatchType} from 'app/store/AppStore';

import {globalHandlers} from 'app/general/globalRequestErrorHandlers';
import {hideLoading} from 'app/general/sweet-loading-bar/slice';
import {accountSettingsSelector} from 'app/settings/api';
import {setReloadRequired} from 'app/settings/slice';
import {showNotification, showToastNotification} from 'app/toasts/slice';
import {getLoginURLWithNext} from 'app/urls';
import {FAKE_TEST_DOMAIN} from 'app/utils/constants';
import {SweetError} from 'app/utils/errors';
import {gt} from 'app/utils/i18n';

import {reportToSentry} from './sentry';
import {getCSRFToken} from './urls';

export type CommonFetchParams = {
  page_size?: number;
  page?: number;
};

// the type of JSON serializable content you can send in postJSON, patchJSON, etc funcs
type RequestBodyType = Record<string, any> | Array<unknown>;

type QueryParams = Record<string, number | string | null | undefined | boolean>;
// common options to be used in the fetch function
const buildCommonOptions = (crossdomain: boolean): RequestInit =>
  crossdomain
    ? {
        mode: 'cors',
      }
    : {
        headers: {
          'X-CSRFToken': getCSRFToken(),
          // django need this to request.is_ajax method works
          'X-Requested-With': 'XMLHttpRequest',
        } as HeadersInit,
        credentials: 'same-origin',
      };

const checkAppVersion = (() => {
  // Check if the response X-HTML-Version matches with the current
  // fronted app version.
  // create a clojure to keep a counter to send report to sentry just once
  return function (response: Response): Response {
    let AppHtmlVersion: undefined | string;

    try {
      AppHtmlVersion =
        accountSettingsSelector(store.getState()).data?.APP_HTML_VERSION ??
        undefined;
    } catch (error) {
      // just catch any error and ignore. I believe any errors would
      // be random temporary ones.
      AppHtmlVersion = undefined;
    }

    const frontVersion = AppHtmlVersion;
    const backVersion = response.headers.get('X-HTML-Version');

    // the version needs be defined on both front and backend to be checked
    // we dont want lock users outside the app if something went wrong
    // getting the app version
    if (frontVersion && backVersion && frontVersion !== backVersion) {
      store.dispatch(setReloadRequired(true));
    }

    return response;
  };
})();

function checkStatus(response: Response): Response | Promise<Response> {
  // handle HTTP errors, since github fetch don't do it
  // https://github.com/github/fetch#handling-http-error-statuses
  if (response.status >= 200 && response.status < 300) {
    return response;
  }

  return checkAndconvertToJson(response).then((data: string | unknown) => {
    const error = new SweetError(
      response.statusText || `Error ${response.status} loading ${response.url}`
    );
    error.response = response;
    error.data = data;
    throw error;
  });
}

function checkAndconvertToJson(response: Response): Promise<string | unknown> {
  // check if the response is in json and convert it to json if it's needed
  return response.headers.get('content-type') === 'application/json'
    ? response.json()
    : response.text();
}

const removeEmptyParams = (
  params: QueryParams // removes params like {fields: undefined}
): Record<string, string | number> => omitBy(params, isUndefined);

export const serialize = (
  // encode a object as a string for submission.
  obj: Record<string, string | number>
): string =>
  // like the https://api.jquery.com/serialize/
  Object.keys(obj)
    .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(obj[k])}`)
    .join('&');

const buildSafeTestURL = (path) => {
  // add the FAKE_TEST_DOMAIN to a path if we're running the tests
  const testing = process.env.NODE_ENV == 'test';
  const isFQDN = path.startsWith('http');
  return `${testing && !isFQDN ? FAKE_TEST_DOMAIN : ''}${path}`;
};

function normalizedFetch(
  url: string,
  options: RequestInit = {},
  crossdomain = false
): Promise<any> {
  // execute the fetch function with common options and common response
  // processors applied
  // add common options to the request
  const mergedOptions = merge({}, buildCommonOptions(crossdomain), options);
  return fetch(buildSafeTestURL(url), mergedOptions)
    .then(checkAppVersion)
    .then(checkStatus)
    .then(checkAndconvertToJson);
}

export function postData<R = unknown>(
  url: string,
  data: Record<string, string | number>,
  opts: RequestInit = {}
): Promise<R> {
  // send a POST request to url with serialized data parameter
  // accept optional fetch func options
  const options = merge(
    {},
    {
      body: serialize(data),
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    },
    opts
  );
  return normalizedFetch(url, options);
}

export function postJson<R = unknown>(
  url: string,
  data: RequestBodyType,
  opts: RequestInit = {}
): Promise<R> {
  // send a POST request to url with data parameter converted to json
  // accept optional fetch func options
  const options = merge(
    {},
    {
      body: JSON.stringify(data),
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    },
    opts
  );
  return normalizedFetch(url, options);
}

export function putJson<R = unknown>(
  url: string,
  data: RequestBodyType,
  opts: RequestInit = {}
): Promise<R> {
  // send a PUT request to url with data parameter converted to json
  // accept optional fetch func options
  const options = merge(
    {},
    {
      body: JSON.stringify(data),
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    },
    opts
  );
  return normalizedFetch(url, options);
}

export function patchJson<R = unknown>(
  url: string,
  data: RequestBodyType,
  opts: RequestInit = {}
): Promise<R> {
  // send a PATCH request to url with data parameter converted to json
  // accept optional fetch func options
  // for compat with older IT systems we disguise the PATCH as POST
  const options = merge(
    {},
    {
      body: JSON.stringify(data),
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-HTTP-Method-Override': 'PATCH',
      },
    },
    opts
  );
  return normalizedFetch(url, options);
}

export function del(
  url: string,
  params: QueryParams = {},
  opts: RequestInit = {}
): Promise<unknown> {
  // send a DELETE request to url, accept optional fetch func options
  const options = merge(
    {},
    {
      method: 'DELETE',
    },
    opts
  );
  const cleanParams = removeEmptyParams(params);
  return normalizedFetch(
    [url, serialize(cleanParams)].filter(Boolean).join('?'),
    options
  );
}

export function get<R>(
  url: string,
  params: QueryParams = {},
  opts: RequestInit = {},
  crossdomain = false
): Promise<R> {
  // send a GET request to url, with params object as GET parameters
  const options = merge(
    {},
    {
      method: 'GET',
    },
    opts
  );
  const cleanParams = removeEmptyParams(params);
  return normalizedFetch(
    [url, serialize(cleanParams)].filter(Boolean).join('?'),
    options,
    crossdomain
  );
}

export function pollUntil(
  requestFunc: () => Promise<any>,
  checkFunc: (response: any) => boolean,
  interval = 5000
): Promise<any> {
  // keep running requestFunc until checkFunc(requestFuncReturn) returns true
  // requestFunc should return a promise
  let cancelled = false;

  // @ts-expect-error onCancel is Bluebird exclusive
  return new Promise((resolve, reject, onCancel) => {
    const doRequest = throttle(
      () =>
        requestFunc().then(
          (response) => {
            if (checkFunc(response)) {
              return resolve(response);
            }

            if (!cancelled) {
              doRequest();
            }
          },
          (error) => {
            reportToSentry(error, {}, true);
            reject(error);
          }
        ),
      interval,
      {
        leading: true,
      }
    );

    if (onCancel != null) {
      onCancel(() => (cancelled = true));
    }

    doRequest();
  });
}

interface RequestErrorHandlers<R> {
  [error: number | string]: (error: SweetError, dispatch: AppDispatchType) => R;
}

export const createErrorHandler =
  <R>(
    fallbackMessage: string,
    dispatch: AppDispatchType,
    handlers: RequestErrorHandlers<R> = {}
  ) =>
  (error: SweetError): R => {
    let response: R;

    const status = error.response?.status;

    if (status != null && status in handlers) {
      response = handlers[status]!(error, dispatch);
    } else if (status != null && status in globalHandlers) {
      response = globalHandlers[status](error, dispatch);
    } else {
      reportToSentry(
        error,
        {
          ...extraFromError(error),
          fallbackMessage,
        },
        true
      );

      if (fallbackMessage) {
        dispatch(showNotification(fallbackMessage, 'error'));
      }

      dispatch(hideLoading());
      throw error;
    }

    dispatch(hideLoading());
    return response;
  };

export const quickLoginErrorHandler = (): RequestErrorHandlers<void> => ({
  '403': () => {
    const next = getLoginURLWithNext();
    window.location.replace(next);
  },
});

export const showForbiddenMessageHandler = (
  throwError: boolean
): RequestErrorHandlers<unknown> => ({
  '403': (error, dispatch) => {
    let message =
      lodashGet(error, 'data.detail') ||
      gt(
        'You are logged out or do not have permission to perform this action.'
      );

    if (message === 'Authentication credentials were not provided.') {
      message = gt(
        'You are logged out or do not have permission to perform this action.'
      );
    } else if (message === 'CSRF Failed: CSRF token missing or incorrect.') {
      message = gt(
        'CSRF cookie was not supplied. Please enable cookies or try another browser.'
      );
    }

    const next = getLoginURLWithNext();
    dispatch(
      showToastNotification(message, {
        level: 'error',
        timeout: 10000,
        link: {
          text: gt('Log in'),
          to: next,
        },
      })
    );

    if (throwError) {
      throw error;
    }
  },
});

export const showNonFieldErrorMessageHandler = (
  throwError: boolean,
  genericErrorMessage: string
): RequestErrorHandlers<void> => ({
  '400': (error, dispatch) => {
    let message =
      lodashGet(error, 'data.non_field_errors[0]') ||
      lodashGet(error, 'data[0].non_field_errors[0]') ||
      lodashGet(error, 'data[0]') ||
      genericErrorMessage;

    // if we failed getting a text error, use the generic one
    if (typeof message !== 'string') {
      message = genericErrorMessage;
    }

    dispatch(showNotification(message, 'error'));

    if (throwError) {
      throw error;
    }
  },
});

export const extraFromError = (
  error: SweetError
): {
  url?: string;
  status?: number;
} => ({
  url: error.response?.url ?? undefined,
  status: error.response?.status ?? undefined,
});
